import { getBezierPath, Position } from '@xyflow/react';

import { type ArrowEdgeRoute, DiagramEdgeRoute } from '../../diagramStyle';
import {
  buildRoundedOrthogonalPath,
  getOrthogonalPathLabelPoint,
  type OrthogonalPathPoint,
} from './orthogonalPath';

export interface RequestedEdgePathInput {
  route: ArrowEdgeRoute;
  source: OrthogonalPathPoint;
  target: OrthogonalPathPoint;
  sourcePosition: Position;
  targetPosition: Position;
  /** Caller-owned interior points, in source-to-target order. */
  waypoints: OrthogonalPathPoint[];
  borderRadius: number;
}

/**
 * Returns the SVG path and label point of an edge whose caller sets its
 * shape, or null for an elbow edge without waypoints. The renderer does not
 * move the waypoints:
 *
 * - Straight: straight segments through each waypoint.
 * - Curved: one smooth curve through each waypoint.
 * - Elbow: horizontal and vertical segments through each waypoint. Between
 *   two points that do not share an axis, the path adds one corner. The
 *   first segment leaves the source along the axis of its handle side, and
 *   the last corner lets the path enter the target along its side's axis.
 */
export function getRequestedEdgePath({
  route,
  source,
  target,
  sourcePosition,
  targetPosition,
  waypoints,
  borderRadius,
}: RequestedEdgePathInput): {
  path: string;
  label: OrthogonalPathPoint;
} | null {
  const points = [source, ...waypoints, target];
  switch (route) {
    case DiagramEdgeRoute.Straight:
      return {
        path: points
          .map(({ x, y }, index) => `${index ? 'L' : 'M'} ${x} ${y}`)
          .join(' '),
        label: getOrthogonalPathLabelPoint(points),
      };
    case DiagramEdgeRoute.Curved: {
      if (waypoints.length) {
        return {
          path: buildCurveThroughPoints(points),
          label: getOrthogonalPathLabelPoint(points),
        };
      }
      const [path, x, y] = getBezierPath({
        sourceX: source.x,
        sourceY: source.y,
        targetX: target.x,
        targetY: target.y,
        sourcePosition,
        targetPosition,
      });
      return { path, label: { x, y } };
    }
    case DiagramEdgeRoute.Elbow: {
      if (!waypoints.length) return null;
      const elbowPoints = addElbowCorners(
        points,
        isVerticalSide(sourcePosition),
        isVerticalSide(targetPosition)
      );
      return {
        path: buildRoundedOrthogonalPath(elbowPoints, borderRadius),
        label: getOrthogonalPathLabelPoint(elbowPoints),
      };
    }
  }
}

/** Returns true for a top or bottom handle, which a path meets vertically. */
export function isVerticalSide(position: Position) {
  return position === Position.Top || position === Position.Bottom;
}

/**
 * Insert one corner between each pair of points that do not share an axis.
 * `vertical` tells whether the next segment leaves the current point
 * vertically. Before the target, it follows the target side instead, so that
 * the arrowhead points into the tile.
 */
export function addElbowCorners(
  points: OrthogonalPathPoint[],
  sourceVertical: boolean,
  targetVertical: boolean
): OrthogonalPathPoint[] {
  // The input always has the source and the target, so the first point exists.
  const result = [points[0]!];
  let vertical = sourceVertical;
  for (const [index, point] of points.slice(1).entries()) {
    const last = result[result.length - 1]!;
    // A corner with a horizontal first segment ends with a vertical segment.
    if (index === points.length - 2) vertical = !targetVertical;
    if (last.x !== point.x && last.y !== point.y) {
      // The corner changes the axis once, so the next segment leaves `point`
      // along the same axis as this pair started.
      result.push(
        vertical ? { x: last.x, y: point.y } : { x: point.x, y: last.y }
      );
    } else {
      // The next segment turns from this horizontal or vertical segment.
      vertical = last.y === point.y;
    }
    result.push(point);
  }
  return result;
}

/**
 * Build a Catmull-Rom spline as cubic Bézier segments. Unlike a curve with
 * the waypoints as control points, it passes through every waypoint.
 */
function buildCurveThroughPoints(points: OrthogonalPathPoint[]): string {
  let path = `M ${points[0]!.x} ${points[0]!.y}`;
  for (let index = 0; index < points.length - 1; index++) {
    // Clamp at the ends: the end point is its own outer neighbor.
    const before = points[Math.max(index - 1, 0)]!;
    const start = points[index]!;
    const end = points[index + 1]!;
    const after = points[Math.min(index + 2, points.length - 1)]!;
    const first = {
      x: start.x + (end.x - before.x) / 6,
      y: start.y + (end.y - before.y) / 6,
    };
    const second = {
      x: end.x - (after.x - start.x) / 6,
      y: end.y - (after.y - start.y) / 6,
    };
    path += ` C ${first.x} ${first.y} ${second.x} ${second.y} ${end.x} ${end.y}`;
  }
  return path;
}
